// IndexedDB Helper für Offline-Daten
const DB_NAME = 'glaserei-crm-offline';
const DB_VERSION = 4; // Erhöht für Arbeitsnachweise-Offline

// Store Names
export const STORES = {
  // Pending Stores (für Write-Operations)
  ZEITERFASSUNG: 'pending-zeiterfassung',
  UNTERSCHRIFTEN: 'pending-unterschriften',
  FOTOS: 'pending-fotos',
  ARBEITSNACHWEISE: 'pending-arbeitsnachweise',

  // Cache Stores (für Read-Operations)
  CACHE_KUNDEN: 'cache-kunden',
  CACHE_TERMINE: 'cache-termine',
  CACHE_ANFRAGEN: 'cache-anfragen',
  CACHE_KATALOG: 'cache-katalog',

  // Legacy
  CACHE: 'data-cache',

  // Sync Metadata
  SYNC_METADATA: 'sync-metadata'
};

// Cache-Dauer in Minuten (24 Stunden)
const CACHE_DURATION_MINUTES = 1440;

// Open IndexedDB connection
export function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB open error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const oldVersion = event.oldVersion;

      console.log(`IndexedDB upgrade from version ${oldVersion} to ${DB_VERSION}`);

      // Pending Zeiterfassung Store
      if (!db.objectStoreNames.contains(STORES.ZEITERFASSUNG)) {
        const store = db.createObjectStore(STORES.ZEITERFASSUNG, {
          keyPath: 'id',
          autoIncrement: true
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('type', 'type', { unique: false });
      }

      // Pending Unterschriften Store
      if (!db.objectStoreNames.contains(STORES.UNTERSCHRIFTEN)) {
        const store = db.createObjectStore(STORES.UNTERSCHRIFTEN, {
          keyPath: 'id',
          autoIncrement: true
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('anfrageId', 'anfrageId', { unique: false });
      }

      // NEU: Pending Fotos Store
      if (!db.objectStoreNames.contains(STORES.FOTOS)) {
        const store = db.createObjectStore(STORES.FOTOS, {
          keyPath: 'id',
          autoIncrement: true
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('kundeId', 'kundeId', { unique: false });
      }

      // NEU: Pending Arbeitsnachweise Store (für Offline-Erstellung)
      if (!db.objectStoreNames.contains(STORES.ARBEITSNACHWEISE)) {
        const store = db.createObjectStore(STORES.ARBEITSNACHWEISE, {
          keyPath: 'id',
          autoIncrement: true
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('anfrageId', 'anfrageId', { unique: false });
        store.createIndex('kundeId', 'kundeId', { unique: false });
      }

      // NEU: Cache Kunden Store
      if (!db.objectStoreNames.contains(STORES.CACHE_KUNDEN)) {
        const store = db.createObjectStore(STORES.CACHE_KUNDEN, {
          keyPath: 'id'
        });
        store.createIndex('email', 'email', { unique: false });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // NEU: Cache Termine Store
      if (!db.objectStoreNames.contains(STORES.CACHE_TERMINE)) {
        const store = db.createObjectStore(STORES.CACHE_TERMINE, {
          keyPath: 'id'
        });
        store.createIndex('kundeId', 'kundeId', { unique: false });
        store.createIndex('datum', 'datum', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // NEU: Cache Anfragen Store
      if (!db.objectStoreNames.contains(STORES.CACHE_ANFRAGEN)) {
        const store = db.createObjectStore(STORES.CACHE_ANFRAGEN, {
          keyPath: 'id'
        });
        store.createIndex('kundeId', 'kundeId', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // NEU: Cache Katalog Store (für Zeiterfassung offline)
      if (!db.objectStoreNames.contains(STORES.CACHE_KATALOG)) {
        const store = db.createObjectStore(STORES.CACHE_KATALOG, {
          keyPath: 'id'
        });
        store.createIndex('einheit', 'einheit', { unique: false });
        store.createIndex('aktiv', 'aktiv', { unique: false });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // Legacy Cache Store (für allgemeine Daten)
      if (!db.objectStoreNames.contains(STORES.CACHE)) {
        const store = db.createObjectStore(STORES.CACHE, {
          keyPath: 'key'
        });
        store.createIndex('expires', 'expires', { unique: false });
      }

      // NEU: Sync Metadata Store
      if (!db.objectStoreNames.contains(STORES.SYNC_METADATA)) {
        db.createObjectStore(STORES.SYNC_METADATA, {
          keyPath: 'key'
        });
      }
    };
  });
}

// ========================================
// PENDING DATA FUNCTIONS (Write-Operations)
// ========================================

// Save data to offline store
export async function saveOffline(storeName, data) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const item = {
        data,
        timestamp: Date.now(),
        synced: false
      };

      const request = store.add(item);

      request.onsuccess = () => {
        console.log(`📴 Offline saved to ${storeName}:`, request.result);
        resolve(request.result);
      };

      request.onerror = () => {
        console.error(`Offline save error in ${storeName}:`, request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('saveOffline error:', error);
    throw error;
  }
}

// Get all offline data from a store
export async function getOfflineData(storeName) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error(`getOfflineData error in ${storeName}:`, request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getOfflineData error:', error);
    return [];
  }
}

// Delete specific offline data by ID
export async function deleteOfflineData(storeName, id) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log(`✅ Deleted from ${storeName}:`, id);
        resolve();
      };

      request.onerror = () => {
        console.error(`Delete error in ${storeName}:`, request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('deleteOfflineData error:', error);
    throw error;
  }
}

// Clear all data from a store
export async function clearOfflineStore(storeName) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        console.log(`Cleared store: ${storeName}`);
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('clearOfflineStore error:', error);
    throw error;
  }
}

// Get count of pending items from a single store
export async function getPendingCountForStore(storeName) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getPendingCountForStore error:', error);
    return 0;
  }
}

// Get total count of ALL pending items
export async function getPendingCount() {
  try {
    const zeiterfassung = await getPendingCountForStore(STORES.ZEITERFASSUNG);
    const unterschriften = await getPendingCountForStore(STORES.UNTERSCHRIFTEN);
    const fotos = await getPendingCountForStore(STORES.FOTOS);
    const arbeitsnachweise = await getPendingCountForStore(STORES.ARBEITSNACHWEISE);
    return zeiterfassung + unterschriften + fotos + arbeitsnachweise;
  } catch (error) {
    console.error('getPendingCount error:', error);
    return 0;
  }
}

// ========================================
// CACHE FUNCTIONS (Read-Operations)
// ========================================

// Cache einzelnes Item
export async function cacheData(storeName, data, customId = null) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const item = {
        ...data,
        id: customId || data.id,
        cachedAt: Date.now()
      };

      const request = store.put(item);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('cacheData error:', error);
    throw error;
  }
}

// Cache multiple Items auf einmal
export async function cacheMultiple(storeName, items) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const cachedAt = Date.now();

      for (const item of items) {
        const cachedItem = {
          ...item,
          cachedAt
        };
        store.put(cachedItem);
      }

      transaction.oncomplete = () => {
        console.log(`📦 ${items.length} Items in ${storeName} gecached`);
        db.close();
        resolve();
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('cacheMultiple error:', error);
    throw error;
  }
}

// Get cached data by ID
export async function getCachedData(storeName, id) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        const item = request.result;
        if (item) {
          resolve(item);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getCachedData error:', error);
    return null;
  }
}

// Get all cached data from a store
export async function getAllCachedData(storeName) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getAllCachedData error:', error);
    return [];
  }
}

// Get cached data by index
export async function getCachedDataByIndex(storeName, indexName, value) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getCachedDataByIndex error:', error);
    return [];
  }
}

// ========================================
// METADATA FUNCTIONS
// ========================================

// Set metadata value
export async function setMetadata(key, value) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_METADATA);

      const request = store.put({ key, value, updatedAt: Date.now() });

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('setMetadata error:', error);
    throw error;
  }
}

// Get metadata value
export async function getMetadata(key) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_METADATA);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result?.value || null);
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getMetadata error:', error);
    return null;
  }
}

// Check if cache is still valid (not expired)
export async function isCacheValid(storeName, maxAgeMinutes = CACHE_DURATION_MINUTES) {
  try {
    const lastSync = await getMetadata(`${storeName}-last-sync`);
    if (!lastSync) return false;

    const maxAge = maxAgeMinutes * 60 * 1000;
    return (Date.now() - lastSync) < maxAge;
  } catch (error) {
    console.error('isCacheValid error:', error);
    return false;
  }
}

// ========================================
// LEGACY FUNCTIONS (Kompatibilität)
// ========================================

// Legacy: Cache data with expiration (für allgemeine Daten)
export async function cacheLegacyData(key, data, expiresInMinutes = 60) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);

      const item = {
        key,
        data,
        expires: Date.now() + (expiresInMinutes * 60 * 1000),
        timestamp: Date.now()
      };

      const request = store.put(item);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('cacheLegacyData error:', error);
    throw error;
  }
}

// Legacy: Get cached data by key
export async function getLegacyCachedData(key) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHE], 'readonly');
      const store = transaction.objectStore(STORES.CACHE);
      const request = store.get(key);

      request.onsuccess = () => {
        const item = request.result;
        if (item && item.expires > Date.now()) {
          resolve(item.data);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('getLegacyCachedData error:', error);
    return null;
  }
}

// Clean up expired cache entries
export async function cleanupExpiredCache() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      const index = store.index('expires');
      const range = IDBKeyRange.upperBound(Date.now());
      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
      };

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        console.log('Expired cache entries cleaned');
        db.close();
        resolve();
      };
    });
  } catch (error) {
    console.error('cleanupExpiredCache error:', error);
  }
}

// ========================================
// HELPER FUNCTIONS
// ========================================

// Check if there's pending offline data
export async function hasPendingData() {
  const total = await getPendingCount();
  return total > 0;
}

// Export all pending data (for debugging)
export async function exportPendingData() {
  const zeiterfassung = await getOfflineData(STORES.ZEITERFASSUNG);
  const unterschriften = await getOfflineData(STORES.UNTERSCHRIFTEN);
  const fotos = await getOfflineData(STORES.FOTOS);
  const arbeitsnachweise = await getOfflineData(STORES.ARBEITSNACHWEISE);
  return {
    zeiterfassung,
    unterschriften,
    fotos,
    arbeitsnachweise,
    exportedAt: new Date().toISOString()
  };
}

// Clear ALL cache stores (for login/org switch)
export async function clearAllCache() {
  console.log('🗑️ Clearing all cache stores...');
  try {
    // Clear all cache stores
    await clearOfflineStore(STORES.CACHE_KUNDEN);
    await clearOfflineStore(STORES.CACHE_TERMINE);
    await clearOfflineStore(STORES.CACHE_ANFRAGEN);
    await clearOfflineStore(STORES.CACHE_KATALOG);
    await clearOfflineStore(STORES.CACHE);
    await clearOfflineStore(STORES.SYNC_METADATA);
    console.log('✅ All cache stores cleared');
  } catch (error) {
    console.error('clearAllCache error:', error);
    // Try to delete entire database as fallback
    try {
      indexedDB.deleteDatabase(DB_NAME);
      console.log('✅ Entire IndexedDB database deleted');
    } catch (e) {
      console.error('Failed to delete IndexedDB:', e);
    }
  }
}

// Get cache stats
export async function getCacheStats() {
  try {
    const kundenCount = (await getAllCachedData(STORES.CACHE_KUNDEN)).length;
    const termineCount = (await getAllCachedData(STORES.CACHE_TERMINE)).length;
    const anfragenCount = (await getAllCachedData(STORES.CACHE_ANFRAGEN)).length;
    const pendingCount = await getPendingCount();

    const kundenLastSync = await getMetadata(`${STORES.CACHE_KUNDEN}-last-sync`);
    const termineLastSync = await getMetadata(`${STORES.CACHE_TERMINE}-last-sync`);

    return {
      cached: {
        kunden: kundenCount,
        termine: termineCount,
        anfragen: anfragenCount
      },
      pending: pendingCount,
      lastSync: {
        kunden: kundenLastSync ? new Date(kundenLastSync).toISOString() : null,
        termine: termineLastSync ? new Date(termineLastSync).toISOString() : null
      }
    };
  } catch (error) {
    console.error('getCacheStats error:', error);
    return null;
  }
}
